import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { courses, AVAILABLE_CITIES } from '../data';

// Maps SEO-friendly URL slugs → course IDs in data.ts
const COURSE_SLUG_MAP: Record<string, string> = {
  'angular': 'angular',
  'java': 'core-java',
  'full-stack-java': 'full-stack-java',
  'python': 'python-programming',
  'full-stack-python': 'full-stack-python',
  'react': 'react',
  'nodejs': 'nodejs',
  'node-js': 'nodejs',
  'aws': 'amazon-web-services-aws',
  'amazon-web-services': 'amazon-web-services-aws',
  'devops': 'devops-with-aws',
  'data-science': 'full-stack-data-science-ai',
  'data-science-ai': 'full-stack-data-science-ai',
  'spring-boot': 'spring-boot-microservices',
  'spring-boot-microservices': 'spring-boot-microservices',
  'power-bi': 'power-bi',
  'cyber-security': 'cyber-security-ethical-hacking',
  'ethical-hacking': 'cyber-security-ethical-hacking',
  'mern-stack': 'mern',
  'mern': 'mern',
  'mean-stack': 'mean',
  'mean': 'mean',
  'html-css-javascript': 'html-css-javascript',
  'html-css-js': 'html-css-javascript',
  'typescript': 'typescript',
  'javascript': 'javascript',
  'c-language': 'c-language',
  'azure': 'ms-azure-azure-devops',
  'azure-devops': 'ms-azure-azure-devops',
  'ui-full-stack-react': 'ui-full-stack-react',
  'data-analytics': 'data-analytics-business-analytics',
  'software-testing': 'full-stack-software-testing',
};

const CITY_GEO: Record<string, { lat: string; lng: string; postal: string }> = {
  hyderabad: { lat: '17.3850', lng: '78.4867', postal: '500081' },
  bangalore: { lat: '12.9716', lng: '77.5946', postal: '560001' },
  mumbai: { lat: '19.0760', lng: '72.8777', postal: '400001' },
  delhi: { lat: '28.6139', lng: '77.2090', postal: '110001' },
  chennai: { lat: '13.0827', lng: '80.2707', postal: '600001' },
  pune: { lat: '18.5204', lng: '73.8567', postal: '411001' },
  noida: { lat: '28.5355', lng: '77.3910', postal: '201301' },
  gurgaon: { lat: '28.4595', lng: '77.0266', postal: '122001' },
};

const FAQ_QUESTIONS = (courseName: string, cityName: string) => [
  {
    q: `What is the duration of ${courseName} training in ${cityName}?`,
    a: `The ${courseName} training in ${cityName} typically runs for 2–6 months depending on the batch schedule — weekday (morning/evening) or weekend options are available. Contact SBRIT Solution for exact batch dates.`,
  },
  {
    q: `Does SBRIT Solution provide placement assistance after ${courseName} training?`,
    a: `Yes. SBRIT Solution provides 100% placement assistance including resume building, mock interviews, interview preparation, and direct company connections. Many students secure jobs within 60 days of completing the ${courseName} course.`,
  },
  {
    q: `Is ${courseName} training in ${cityName} available online?`,
    a: `Yes. Both classroom training at our ${cityName} centre and live online training are available. Online batches follow the same curriculum with live instructor-led sessions.`,
  },
  {
    q: `What is the fee for ${courseName} course in ${cityName}?`,
    a: `The fee for ${courseName} training in ${cityName} varies by course duration and batch. SBRIT Solution offers competitive pricing with EMI options. Contact us for the latest fee structure and discounts.`,
  },
  {
    q: `Who are the trainers for ${courseName} at SBRIT Solution ${cityName}?`,
    a: `All ${courseName} trainers at SBRIT Solution are working professionals with 7–15 years of industry experience. They bring real-world projects and current industry practices into every class.`,
  },
  {
    q: `What certificate will I get after completing ${courseName} training?`,
    a: `On successful completion you receive an SBRIT Solution ${courseName} certification recognised by leading IT companies. The certificate covers theoretical knowledge and project-based practical skills.`,
  },
];

const DynamicCityCoursePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // Parse slug: "angular-training-in-bangalore"
  const match = slug?.match(/^(.+)-training-in-(.+)$/);
  if (!match) return <Navigate to="/" replace />;

  const courseSlug = match[1]; // e.g. "angular"
  const citySlug = match[2];   // e.g. "bangalore"

  const courseId = COURSE_SLUG_MAP[courseSlug];
  const course = courseId
    ? courses.find(c => c.id === courseId)
    : courses.find(c => c.id === courseSlug);

  const cityData = AVAILABLE_CITIES.find(c => c.id === citySlug);

  if (!course || !cityData) return <Navigate to="/courses" replace />;

  const cityName = cityData.name;
  const courseName = course.title;
  const geo = CITY_GEO[citySlug] || CITY_GEO['hyderabad'];
  const pageUrl = `https://sbritsolution.in/${slug}`;
  const faqs = FAQ_QUESTIONS(courseName, cityName);

  // --- Structured Data ---
  const courseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${courseName} Training in ${cityName}`,
    description: `Comprehensive ${courseName} training in ${cityName} by SBRIT Solution. Industry experts, hands-on projects, 100% placement assistance, and certification.`,
    provider: {
      '@type': 'EducationalOrganization',
      name: 'SBRIT Solution',
      url: 'https://sbritsolution.in',
      logo: 'https://sbritsolution.in/sbritsolution.in.png',
    },
    offers: {
      '@type': 'Offer',
      price: course.price?.toString() || '14999',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: ['onsite', 'online'],
      location: {
        '@type': 'Place',
        name: `SBRIT Solution - ${cityName}`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: cityData.address,
          addressLocality: cityName,
          addressRegion: cityData.state,
          addressCountry: 'IN',
        },
      },
    },
    teaches: course.topics || [],
    educationalLevel: 'Beginner to Advanced',
    inLanguage: 'en',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '350',
      bestRating: '5',
    },
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `SBRIT Solution - ${courseName} Training in ${cityName}`,
    image: 'https://sbritsolution.in/sbritsolution.in.png',
    description: `Best ${courseName} Training Institute in ${cityName}. Learn ${courseName} from industry experts with hands-on projects, 100% placement assistance, and certification.`,
    url: pageUrl,
    telephone: '+91-9000000000',
    email: 'info@sbritsolution.in',
    priceRange: `₹${(course.price || 14999).toLocaleString()}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: cityData.address,
      addressLocality: cityName,
      addressRegion: cityData.state,
      postalCode: geo.postal,
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: geo.lat,
      longitude: geo.lng,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '19:00',
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sbritsolution.in' },
      { '@type': 'ListItem', position: 2, name: 'Courses', item: 'https://sbritsolution.in/courses' },
      { '@type': 'ListItem', position: 3, name: `${courseName} Training in ${cityName}`, item: pageUrl },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const title = `${courseName} Training in ${cityName} | Best ${courseName} Course with Placement | SBRIT Solution`;
  const description = `Join the best ${courseName} training in ${cityName} at SBRIT Solution. ✅ Industry-expert trainers ✅ Hands-on projects ✅ 100% placement assistance ✅ Classroom & online batches ✅ ${courseName} certification. Enroll now!`;
  const keywords = `${courseName.toLowerCase()} training in ${cityName.toLowerCase()}, best ${courseName.toLowerCase()} course ${cityName.toLowerCase()}, ${courseName.toLowerCase()} classes ${cityName.toLowerCase()}, ${courseName.toLowerCase()} institute ${cityName.toLowerCase()}, ${courseName.toLowerCase()} certification ${cityName.toLowerCase()}, ${courseName.toLowerCase()} training with placement ${cityName.toLowerCase()}, learn ${courseName.toLowerCase()} ${cityName.toLowerCase()}, ${courseName.toLowerCase()} job oriented course ${cityName.toLowerCase()}, ${courseName.toLowerCase()} online training ${cityName.toLowerCase()}, SBRIT Solution ${cityName.toLowerCase()}`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://sbritsolution.in/sbritsolution.in.png" />
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content={cityName} />
        <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">

        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200 py-3">
          <div className="container mx-auto px-4">
            <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
              <Link to="/" className="hover:text-blue-600">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/courses" className="hover:text-blue-600">Courses</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900 font-medium">{courseName} Training in {cityName}</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' }} className="text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-block bg-white bg-opacity-20 rounded-full px-4 py-2 text-sm font-medium mb-4">
              🎓 Best IT Training Institute in {cityName}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              {courseName} Training in {cityName}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-6 max-w-3xl mx-auto">
              Industry-Expert Led {courseName} Course in {cityName} with 100% Placement Assistance
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8 text-sm">
              <span className="bg-white bg-opacity-20 rounded-full px-4 py-2">✅ Live Projects</span>
              <span className="bg-white bg-opacity-20 rounded-full px-4 py-2">✅ Job Placement</span>
              <span className="bg-white bg-opacity-20 rounded-full px-4 py-2">✅ Certification</span>
              <span className="bg-white bg-opacity-20 rounded-full px-4 py-2">✅ Weekend Batches</span>
              <span className="bg-white bg-opacity-20 rounded-full px-4 py-2">✅ Online Available</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/enroll"
                className="bg-white text-blue-700 font-bold py-3 px-8 rounded-lg hover:bg-blue-50 transition-colors text-lg"
              >
                Enroll Now — Next Batch {course.date}
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white font-bold py-3 px-8 rounded-lg hover:bg-white hover:text-blue-700 transition-colors text-lg"
              >
                Free Counselling
              </Link>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-blue-600">500+</div>
                <div className="text-gray-600 text-sm mt-1">Students Placed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">4.8★</div>
                <div className="text-gray-600 text-sm mt-1">Average Rating</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">10+</div>
                <div className="text-gray-600 text-sm mt-1">Live Projects</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">7+</div>
                <div className="text-gray-600 text-sm mt-1">Years Experience</div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">

              {/* About the Course */}
              <section className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  About {courseName} Training in {cityName}
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  SBRIT Solution offers the most comprehensive <strong>{courseName} training in {cityName}</strong> designed
                  for both beginners and working professionals. Our {courseName} course in {cityName} is built around real-world
                  industry requirements so that every student graduates job-ready.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The {courseName} program at our <strong>{cityName} training centre</strong> combines
                  theoretical foundations with hands-on coding practice. You work on live projects under the guidance of
                  trainers who are currently working in top MNCs and product companies. This ensures the curriculum stays
                  current with what employers actually look for in {courseName} candidates.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you prefer <strong>{courseName} classroom training in {cityName}</strong> or a live online
                  batch from anywhere in India, SBRIT Solution gives you the same high-quality learning experience.
                  Flexible batch timings — morning, evening, and weekend — make it easy to learn {courseName} alongside
                  your college or job.
                </p>
              </section>

              {/* Curriculum */}
              <section className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {courseName} Course Curriculum in {cityName}
                </h2>
                <p className="text-gray-600 mb-6">
                  Our {courseName} syllabus covers everything from fundamentals to advanced concepts — aligned with
                  current {cityName} job market requirements.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {course.topics.map((topic, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <span className="text-blue-600 font-bold mt-0.5 flex-shrink-0">{i + 1}.</span>
                      <span className="text-gray-800 font-medium">{topic}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">
                    ✅ Curriculum updated quarterly to match {cityName} industry standards
                  </p>
                </div>
              </section>

              {/* Why Choose SBRIT */}
              <section className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Why Choose SBRIT Solution for {courseName} Training in {cityName}?
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: '👨‍💻', title: 'Industry-Expert Trainers', desc: `Trainers with 7–15 years of ${courseName} industry experience currently working in top companies.` },
                    { icon: '🏗️', title: 'Real-World Projects', desc: `Work on 10+ live ${courseName} projects used by actual businesses — not just tutorials.` },
                    { icon: '💼', title: '100% Placement Assistance', desc: `Dedicated placement team connects you with top IT companies in ${cityName} and across India.` },
                    { icon: '📅', title: 'Flexible Batch Timings', desc: 'Morning, evening, and weekend batches so you can learn without disrupting your current schedule.' },
                    { icon: '🖥️', title: 'Classroom + Online', desc: `Both in-person ${cityName} centre training and live online options — same quality, your choice.` },
                    { icon: '📜', title: 'Industry-Recognised Certificate', desc: `Receive an ${courseName} certification from SBRIT Solution that employers in ${cityName} trust.` },
                    { icon: '🎯', title: 'Interview Preparation', desc: 'Mock interviews, resume building, LinkedIn profile optimisation, and HR round coaching.' },
                    { icon: '♾️', title: 'Lifetime Access', desc: 'Lifetime access to updated course materials, recordings, and placement support even after you join a company.' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 border border-gray-100 rounded-lg hover:border-blue-200 transition-colors">
                      <span className="text-2xl flex-shrink-0">{item.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900">{item.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Training Center Info */}
              <section className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {courseName} Training Centre in {cityName}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">📍 Address</h3>
                    <p className="text-gray-700">{cityData.address}, {cityName}, {cityData.state}</p>
                    <h3 className="font-semibold text-gray-900 mt-4 mb-2">📞 Contact</h3>
                    <p className="text-gray-700">info@sbritsolution.in</p>
                    <h3 className="font-semibold text-gray-900 mt-4 mb-2">🕐 Timings</h3>
                    <p className="text-gray-700">Mon–Sat: 9:00 AM – 7:00 PM</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">🎯 Batch Options</h3>
                    <ul className="text-gray-700 space-y-2">
                      <li>• Weekday Morning: 7:00 AM – 10:00 AM</li>
                      <li>• Weekday Evening: 6:00 PM – 9:00 PM</li>
                      <li>• Weekend: Saturday & Sunday</li>
                      <li>• Live Online Batches: All timings</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Testimonial */}
              {course.testimonial && (
                <section className="bg-blue-50 border border-blue-200 rounded-xl p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Student Reviews</h2>
                  <blockquote className="text-gray-800 text-lg italic leading-relaxed">
                    "{course.testimonial.replace(/^"|"$/g, '')}"
                  </blockquote>
                  <p className="mt-4 text-blue-700 font-medium">
                    ⭐⭐⭐⭐⭐ — {courseName} Training, {cityName}
                  </p>
                </section>
              )}

              {/* FAQ */}
              <section className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Frequently Asked Questions — {courseName} Training in {cityName}
                </h2>
                <div className="space-y-5">
                  {faqs.map((faq, i) => (
                    <div key={i} className="border-b border-gray-100 pb-5 last:border-0 last:pb-0">
                      <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>

            </div>

            {/* Sidebar */}
            <div className="space-y-6">

              {/* Price Card */}
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-500 mb-1">Course Fee</p>
                  <p className="text-4xl font-bold text-blue-600">
                    ₹{(course.price || 14999).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">EMI options available</p>
                </div>

                <div className="space-y-3 mb-6">
                  {[
                    { label: 'Duration', value: course.duration || '2–4 Months' },
                    { label: 'Mode', value: course.mode || 'Classroom & Online' },
                    { label: 'Batch Size', value: course.batchSize || '12–18 Students' },
                    { label: 'Next Batch', value: course.date },
                    { label: 'Certificate', value: 'SBRIT Solution' },
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-gray-100 pb-2">
                      <span className="text-gray-500">{item.label}</span>
                      <span className="text-gray-900 font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/enroll"
                  className="block w-full bg-blue-600 text-white text-center font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors mb-3"
                >
                  Enroll Now
                </Link>
                <Link
                  to="/contact"
                  className="block w-full border-2 border-blue-600 text-blue-600 text-center font-bold py-3 px-6 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Get Free Counselling
                </Link>
              </div>

              {/* Other Cities */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-bold text-gray-900 mb-4">
                  {courseName} Training in Other Cities
                </h3>
                <div className="space-y-2">
                  {AVAILABLE_CITIES.filter(c => c.id !== citySlug).map(c => (
                    <Link
                      key={c.id}
                      to={`/${courseSlug}-training-in-${c.id}`}
                      className="flex justify-between items-center py-2 border-b border-gray-100 hover:text-blue-600 transition-colors text-sm"
                    >
                      <span>{courseName} Training in {c.name}</span>
                      <span className="text-blue-500">→</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Related Courses */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-bold text-gray-900 mb-4">Related Courses in {cityName}</h3>
                <div className="space-y-2">
                  {courses.slice(0, 6).filter(c => c.id !== course.id).map(c => (
                    <Link
                      key={c.id}
                      to={`/course/${c.id}`}
                      className="flex justify-between items-center py-2 border-b border-gray-100 hover:text-blue-600 transition-colors text-sm"
                    >
                      <span>{c.title}</span>
                      <span className="text-blue-500">→</span>
                    </Link>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* CTA Bottom */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white py-16 text-center">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-4">
              Start Your {courseName} Career in {cityName} Today
            </h2>
            <p className="text-blue-200 text-xl mb-8 max-w-2xl mx-auto">
              Join 500+ professionals who have transformed their careers with SBRIT Solution's
              {' '}{courseName} training in {cityName}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/enroll"
                className="bg-white text-blue-700 font-bold py-3 px-8 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Enroll Now
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white font-bold py-3 px-8 rounded-lg hover:bg-white hover:text-blue-700 transition-colors"
              >
                Talk to Counsellor
              </Link>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default DynamicCityCoursePage;
